import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Divider,
  LinearProgress,
  Typography,
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded'
import PersonIcon from '@mui/icons-material/Person'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import BlockIcon from '@mui/icons-material/Block'

function getRemainingSeconds(targetTime, nowTs) {
  if (!targetTime) return null
  const targetMs = new Date(targetTime).getTime()
  if (Number.isNaN(targetMs)) return null
  return Math.max(0, Math.ceil((targetMs - nowTs) / 1000))
}

function formatCountdown(seconds) {
  if (seconds == null) return '--:--'
  const safeSeconds = Math.max(0, seconds)
  const minutesPart = Math.floor(safeSeconds / 60)
  const secondsPart = safeSeconds % 60
  return `${String(minutesPart).padStart(2, '0')}:${String(secondsPart).padStart(2, '0')}`
}

export default function CheckinRequestModal({
  open,
  requestData,
  onCheckout,
  onIgnore,
  onClose,
  loading = false,
  nowTs = Date.now(),
}) {
  if (!requestData) {
    return null
  }

  const requesterName = requestData?.requestingPractitionerName || 'Another practitioner'
  const isCountdownActive = requestData?.status === 'CHECKOUT_PENDING'
  const responseWindowSeconds = getRemainingSeconds(requestData?.expiresAt, nowTs)
  const checkoutSeconds = getRemainingSeconds(requestData?.countdownEndsAt, nowTs)
  const roomLabel = [requestData?.clinicBrandName, requestData?.clinicLocationName, requestData?.chamberRoomTitle]
    .filter(Boolean)
    .join(' > ')
  const title = isCountdownActive ? 'Performing Checkout' : 'Checkout Requested'
  const countdownLabel = isCountdownActive
    ? `Performing checkout in ${formatCountdown(checkoutSeconds)}`
    : `Respond in ${formatCountdown(responseWindowSeconds)}`
  const progressValue =
    isCountdownActive && checkoutSeconds != null
      ? ((10 - Math.min(checkoutSeconds, 10)) / 10) * 100
      : responseWindowSeconds != null
      ? ((120 - Math.min(responseWindowSeconds, 120)) / 120) * 100
      : 0

  return (
    <Dialog
      open={open}
      onClose={!loading && !isCountdownActive ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: '1px solid rgba(37, 99, 235, 0.12)',
              background:
                'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)',
              display: 'grid',
              gap: 1.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Practitioner waiting for this room
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {requesterName}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MeetingRoomRoundedIcon color="primary" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Chamber room
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {roomLabel || 'Assigned chamber room'}
                </Typography>
              </Box>
            </Box>

            {requestData?.reason ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                Note: {requestData.reason}
              </Typography>
            ) : null}
          </Box>

          <Divider />

          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: `1px solid ${
                isCountdownActive ? 'rgba(225, 29, 72, 0.18)' : 'rgba(37, 99, 235, 0.12)'
              }`,
              background: isCountdownActive
                ? 'linear-gradient(135deg, rgba(225, 29, 72, 0.12) 0%, rgba(255, 255, 255, 0.98) 100%)'
                : 'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccessTimeIcon
                sx={{
                  color: isCountdownActive ? 'rgba(190, 24, 93, 0.96)' : 'rgba(37, 99, 235, 0.96)',
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  color: isCountdownActive ? 'rgba(190, 24, 93, 0.96)' : 'rgba(37, 99, 235, 0.96)',
                }}
              >
                {countdownLabel}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, progressValue))}
              sx={{
                height: 8,
                borderRadius: '999px',
                backgroundColor: isCountdownActive
                  ? 'rgba(225, 29, 72, 0.08)'
                  : 'rgba(37, 99, 235, 0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: '999px',
                  backgroundColor: isCountdownActive
                    ? 'rgba(225, 29, 72, 0.92)'
                    : 'rgba(37, 99, 235, 0.92)',
                },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {isCountdownActive
                ? 'You still have a few seconds to rethink, but the checkout is already underway.'
                : 'If you do not act, an automatic checkout countdown will begin after the response window closes.'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {isCountdownActive ? (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
            This modal will close after checkout completes.
          </Typography>
        ) : (
          <>
            <Button
              onClick={onIgnore}
              variant="outlined"
              color="inherit"
              startIcon={<BlockIcon />}
              disabled={loading}
              sx={{ flex: 1, textTransform: 'none', borderRadius: '999px' }}
            >
              Stay Checked In
            </Button>
            <Button
              onClick={onCheckout}
              variant="contained"
              color="primary"
              startIcon={<ExitToAppIcon />}
              disabled={loading}
              sx={{ flex: 1, textTransform: 'none', borderRadius: '999px' }}
            >
              {loading ? 'Starting checkout...' : 'Check Out'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}








